/**
 * English tokeniser (ENGINE-SPEC §2.5). Hand-written scanner, one pass, no regex per token.
 *
 *   WORD  = [\p{L}\p{M}]+(?:['’][\p{L}\p{M}]+)*(?:-[\p{L}\p{M}]+)*   (must contain a \p{L}: a bare
 *           run of combining marks, e.g. an emoji variation selector, is not a word)
 *   NUM   = \p{Nd}+(?:[.,:]\p{Nd}+)*%?
 *   PUNCT = one \p{P} or \p{S} code point
 *   ELLIPSIS = a run of 3+ full stops, or one "…"        DASH = "--" or "---"
 *   space, control, format (zero-width) and unassigned characters emit nothing (they only separate tokens)
 *
 * Interned form (§2.1): the token text with ’ turned into ', then lower-cased. NBSP is a separator, so it is never
 * inside a token. Zero-width characters cannot occur inside a word either, because N7 (document/hygiene.ts) already
 * removed the word-internal ones from `doc.text`; one that N7 keeps (beside a dash, in an emoji or Indic sequence) is
 * a format character and only separates tokens. Look-alike Cyrillic letters are mapped to Latin by N8 before this
 * scanner runs.
 */
import { TOK_DASH, TOK_ELLIPSIS, TOK_NUM, TOK_PUNCT, TOK_WORD } from '../../document/types.ts';
import type { TokenBuffer } from '../types.ts';

const OTHER = 0;
const LETTER = 1;
const DIGIT = 2;
const APOS = 3;
const HYPHEN = 4;
const SPACE = 5;
const PUNCT = 6;
const MARK = 7;
const DOT = 8;
const ELL = 9;

const ASCII = new Uint8Array(128);
for (let c = 65; c <= 90; c++) ASCII[c] = LETTER;
for (let c = 97; c <= 122; c++) ASCII[c] = LETTER;
for (let c = 48; c <= 57; c++) ASCII[c] = DIGIT;
for (let c = 33; c <= 126; c++) if (ASCII[c] === OTHER) ASCII[c] = PUNCT;
ASCII[39] = APOS;
ASCII[45] = HYPHEN;
ASCII[46] = DOT;
for (const c of [9, 10, 11, 12, 13, 32]) ASCII[c] = SPACE;

const RE_L = /\p{L}/u;
const RE_M = /\p{M}/u;
const RE_ND = /\p{Nd}/u;
const RE_Z = /\p{Z}/u;
const RE_PS = /[\p{P}\p{S}]/u;

/** Class of a BMP code unit >= 128 (cached). Surrogates are classified as OTHER here. */
const bmp = new Uint8Array(65536);
const bmpKnown = new Uint8Array(65536);
function classOfCodePoint(cp: number): number {
	if (cp === 0x2019) return APOS;
	if (cp === 0x2026) return ELL;
	if (cp === 0x85) return SPACE;
	const s = String.fromCodePoint(cp);
	if (RE_L.test(s)) return LETTER;
	if (RE_M.test(s)) return MARK;
	if (RE_ND.test(s)) return DIGIT;
	if (RE_Z.test(s)) return SPACE;
	if (RE_PS.test(s)) return PUNCT;
	return OTHER;
}
function classBmp(c: number): number {
	if (bmpKnown[c] === 1) return bmp[c] as number;
	const k = c >= 0xd800 && c <= 0xdfff ? OTHER : classOfCodePoint(c);
	bmp[c] = k;
	bmpKnown[c] = 1;
	return k;
}
const astral = new Map<number, number>();
function classAstral(cp: number): number {
	let k = astral.get(cp);
	if (k === undefined) {
		k = classOfCodePoint(cp);
		astral.set(cp, k);
	}
	return k;
}

/** Class at `i` and its width in UTF-16 units, packed as class | (width << 8). */
function classAt(text: string, i: number, n: number): number {
	const c = text.charCodeAt(i);
	if (c < 128) return ASCII[c] as number;
	if (c >= 0xd800 && c <= 0xdbff && i + 1 < n) {
		const d = text.charCodeAt(i + 1);
		if (d >= 0xdc00 && d <= 0xdfff) {
			const cp = (c - 0xd800) * 0x400 + (d - 0xdc00) + 0x10000;
			return classAstral(cp) | (2 << 8);
		}
	}
	return classBmp(c);
}

function scanDigits(text: string, from: number, n: number): number {
	let j = from;
	while (j < n) {
		const p = classAt(text, j, n);
		if ((p & 0xff) !== DIGIT) break;
		j += p >> 8 || 1;
	}
	return j;
}

const MAX_LEN = 65535;

export function tokeniseEnglish(text: string, offset: number, out: TokenBuffer): void {
	const n = text.length;
	let i = 0;
	while (i < n) {
		const c = text.charCodeAt(i);
		let packed: number;
		if (c < 128) packed = ASCII[c] as number;
		else packed = classAt(text, i, n);
		const k = packed & 0xff;
		const w = packed >> 8 || 1;
		switch (k) {
			case SPACE:
			case OTHER:
				i += w;
				break;
			case LETTER:
			case MARK: {
				let j = i;
				let hasLetter = false;
				let curly = false;
				// letters and marks
				for (;;) {
					if (j >= n) break;
					const p = classAt(text, j, n);
					const pk = p & 0xff;
					if (pk === LETTER) hasLetter = true;
					else if (pk !== MARK) break;
					j += p >> 8 || 1;
				}
				// (['’] letters+)*
				for (;;) {
					if (j + 1 >= n) break;
					const a = text.charCodeAt(j);
					if (a !== 39 && a !== 0x2019) break;
					const p = classAt(text, j + 1, n);
					const pk = p & 0xff;
					if (pk !== LETTER && pk !== MARK) break;
					if (a === 0x2019) curly = true;
					j += 1;
					for (;;) {
						if (j >= n) break;
						const q = classAt(text, j, n);
						const qk = q & 0xff;
						if (qk === LETTER) hasLetter = true;
						else if (qk !== MARK) break;
						j += q >> 8 || 1;
					}
				}
				// (-letters+)*
				for (;;) {
					if (j + 1 >= n) break;
					if (text.charCodeAt(j) !== 45) break;
					const p = classAt(text, j + 1, n);
					const pk = p & 0xff;
					if (pk !== LETTER && pk !== MARK) break;
					j += 1;
					for (;;) {
						if (j >= n) break;
						const q = classAt(text, j, n);
						const qk = q & 0xff;
						if (qk === LETTER) hasLetter = true;
						else if (qk !== MARK) break;
						j += q >> 8 || 1;
					}
				}
				if (j - i > MAX_LEN) j = i + MAX_LEN;
				if (hasLetter) {
					let f = text.slice(i, j);
					if (curly) f = f.replace(/’/g, "'");
					out.push(offset + i, j - i, out.intern(f.toLowerCase()), TOK_WORD);
				}
				i = j;
				break;
			}
			case DIGIT: {
				let j = scanDigits(text, i, n);
				for (;;) {
					if (j + 1 >= n) break;
					const a = text.charCodeAt(j);
					if (a !== 46 && a !== 44 && a !== 58) break;
					if ((classAt(text, j + 1, n) & 0xff) !== DIGIT) break;
					j = scanDigits(text, j + 1, n);
				}
				if (j < n && text.charCodeAt(j) === 37) j += 1;
				if (j - i > MAX_LEN) j = i + MAX_LEN;
				out.push(offset + i, j - i, out.intern(text.slice(i, j)), TOK_NUM);
				i = j;
				break;
			}
			case HYPHEN: {
				let j = i + 1;
				while (j < n && j - i < 3 && text.charCodeAt(j) === 45) j++;
				if (j - i >= 2) {
					out.push(offset + i, j - i, out.intern(text.slice(i, j)), TOK_DASH);
					i = j;
				} else {
					out.push(offset + i, 1, out.intern('-'), TOK_PUNCT);
					i += 1;
				}
				break;
			}
			case DOT: {
				let j = i + 1;
				while (j < n && text.charCodeAt(j) === 46) j++;
				if (j - i >= 3) {
					const len = Math.min(j - i, MAX_LEN);
					out.push(offset + i, len, out.intern(text.slice(i, i + len)), TOK_ELLIPSIS);
					i += len;
				} else {
					out.push(offset + i, 1, out.intern('.'), TOK_PUNCT);
					i += 1;
				}
				break;
			}
			case ELL:
				out.push(offset + i, 1, out.intern('…'), TOK_ELLIPSIS);
				i += 1;
				break;
			default: {
				// APOS not inside a word, and PUNCT: one code point.
				const s = text.slice(i, i + w);
				out.push(offset + i, w, out.intern(c === 0x2018 || c === 0x2019 ? "'" : s), TOK_PUNCT);
				i += w;
			}
		}
	}
}
